<div class="mfa-enrollment">
  <!-- Step 0: verify email -->
  <ng-container *ngIf="step() === 'verify-email'">
    <div class="mfa-enrollment-intro">
      <span class="mfa-enrollment-icon"><fa-icon [icon]="faEnvelope"></fa-icon></span>
      <div>
        <p class="mfa-enrollment-step-title">Confirm your email address</p>
        <p class="mfa-enrollment-step-copy mb-0">
          Before you set up two-factor authentication, we need to make sure <strong>{{ email }}</strong> is yours.
        </p>
      </div>
    </div>

    <div class="alert alert-success mb-0" *ngIf="verificationEmailSent()">
      We sent a link to <strong>{{ email }}</strong>. Open it from your inbox, then come back here and continue.
    </div>

    <div class="mfa-enrollment-error" *ngIf="errorMessage()">
      <fa-icon [icon]="faTriangleExclamation"></fa-icon> {{ errorMessage() }}
    </div>

    <div class="mfa-enrollment-actions">
      <button type="button" class="btn btn-outline-dark" *ngIf="showCancel" (click)="cancel()" [disabled]="working()">
        {{ cancelLabel }}
      </button>
      <button type="button" class="btn btn-outline-primary" *ngIf="verificationEmailSent()" (click)="sendVerificationEmail()" [disabled]="working()">
        <span>Send again</span>
      </button>
      <button type="button" class="btn btn-primary" *ngIf="!verificationEmailSent()" (click)="sendVerificationEmail()" [disabled]="working()">
        <fa-icon *ngIf="working()" [icon]="faSpinnerThird" animation="spin"></fa-icon>
        <span>Email me a link</span>
      </button>
      <button type="button" class="btn btn-primary" *ngIf="verificationEmailSent()" (click)="checkEmailVerified()" [disabled]="working()">
        <fa-icon *ngIf="working()" [icon]="faSpinnerThird" animation="spin"></fa-icon>
        <span>I've opened the link</span>
      </button>
    </div>
  </ng-container>

  <!-- Step 1: confirm password -->
  <ng-container *ngIf="step() === 'reauth'">
    <div class="mfa-enrollment-intro">
      <span class="mfa-enrollment-icon"><fa-icon [icon]="faShieldHalved"></fa-icon></span>
      <p *ngIf="usesPassword()">To keep your account secure, please confirm your password before setting up two-factor authentication.</p>
      <p *ngIf="!usesPassword()">To keep your account secure, please confirm your Google account before setting up two-factor authentication.</p>
    </div>

    <form (ngSubmit)="submitReauth()">
      <ng-container *ngIf="usesPassword()">
        <label class="form-label" for="mfa-reauth-password">Password</label>
        <input
          id="mfa-reauth-password"
          class="form-control"
          type="password"
          autocomplete="current-password"
          [(ngModel)]="password"
          name="password"
          [disabled]="working()"
          autofocus
        />
      </ng-container>

      <div class="mfa-enrollment-error" *ngIf="errorMessage()">
        <fa-icon [icon]="faTriangleExclamation"></fa-icon> {{ errorMessage() }}
      </div>

      <div class="mfa-enrollment-actions">
        <button type="button" class="btn btn-outline-dark" *ngIf="showCancel" (click)="cancel()" [disabled]="working()">
          {{ cancelLabel }}
        </button>
        <button type="submit" class="btn btn-primary" [disabled]="working() || (usesPassword() && !password)">
          <fa-icon *ngIf="working()" [icon]="faSpinnerThird" animation="spin"></fa-icon>
          <span>{{ usesPassword() ? 'Continue' : 'Continue with Google' }}</span>
        </button>
      </div>
    </form>
  </ng-container>

  <!-- Step 1b: confirm existing authenticator when adding another device -->
  <ng-container *ngIf="step() === 'second-factor'">
    <div class="mfa-enrollment-intro">
      <span class="mfa-enrollment-icon"><fa-icon [icon]="faShieldHalved"></fa-icon></span>
      <div>
        <p class="mfa-enrollment-step-title">Confirm your existing device</p>
        <p class="mfa-enrollment-step-copy mb-0" *ngIf="reauthHints.length < 2">
          Enter the 6-digit code from your authenticator app before adding a new device.
        </p>
        <p class="mfa-enrollment-step-copy mb-0" *ngIf="reauthHints.length > 1">
          Enter the 6-digit code from <strong>{{ selectedReauthHintLabel }}</strong> before adding a new device.
          <button
            type="button"
            class="btn btn-link btn-sm p-0 align-baseline"
            *ngIf="!showReauthHintPicker"
            (click)="showReauthHintPicker = true"
            [disabled]="working()"
          >
            Use a different device
          </button>
        </p>
      </div>
    </div>

    <form (ngSubmit)="submitSecondFactor()">
      <div class="mfa-enrollment-hint-picker" *ngIf="showReauthHintPicker">
        <div class="form-check" *ngFor="let hint of reauthHints">
          <input
            class="form-check-input"
            type="radio"
            name="reauthHint"
            [id]="'mfa-reauth-hint-' + hint.uid"
            [value]="hint.uid"
            [checked]="hint.uid === reauthSelectedHintUid"
            (change)="selectReauthHint(hint.uid)"
            [disabled]="working()"
          >
          <label class="form-check-label" [for]="'mfa-reauth-hint-' + hint.uid">
            {{ hint.displayName || 'Authenticator app' }}
          </label>
        </div>
      </div>

      <input
        class="form-control mfa-code-input"
        [class.mfa-code-input-error]="errorMessage()"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="7"
        pattern="[0-9 ]*"
        spellcheck="false"
        placeholder="000 000"
        aria-label="6-digit authenticator code"
        [attr.aria-invalid]="errorMessage() ? 'true' : null"
        [ngModel]="reauthCode"
        (input)="onReauthCodeInput($event)"
        name="reauthCode"
        [disabled]="working()"
        autofocus
      />

      <div class="mfa-enrollment-error" *ngIf="errorMessage()">
        <fa-icon [icon]="faTriangleExclamation"></fa-icon> {{ errorMessage() }}
      </div>

      <div class="mfa-enrollment-actions">
        <button type="button" class="btn btn-outline-dark" *ngIf="showCancel" (click)="cancel()" [disabled]="working()">
          {{ cancelLabel }}
        </button>
        <button type="submit" class="btn btn-primary" [disabled]="working() || !isOtpComplete(reauthCode)">
          <fa-icon *ngIf="working()" [icon]="faSpinnerThird" animation="spin"></fa-icon>
          <span>Verify</span>
        </button>
      </div>
    </form>
  </ng-container>

  <!-- Step 2: add to authenticator + verify -->
  <ng-container *ngIf="step() === 'setup'">
    <ol class="mfa-enrollment-steps">
      <li>
        <p class="mfa-enrollment-step-title">Open your authenticator app</p>
        <p class="mfa-enrollment-step-copy">
          Already have one on your phone? Great, use that. Need one? Choose a trusted app below and install it on your phone.
        </p>
        <div class="mfa-enrollment-app-list">
          <button
            type="button"
            class="btn mfa-enrollment-app"
            *ngFor="let app of authenticatorApps"
            (click)="openAuthenticatorApp(app)"
            [attr.aria-label]="'See how to install ' + app.name + ' on your phone'"
          >
            <img class="mfa-auth-app-icon" [src]="app.iconUrl" alt="" width="44" height="44">
            <span class="mfa-enrollment-app-name">{{ app.name }}</span>
          </button>
        </div>
      </li>
      <li>
        <p class="mfa-enrollment-step-title">Scan the QR code</p>
        <p class="mfa-enrollment-step-copy">
          In the app, tap <strong>Add account</strong> or <strong>Scan QR code</strong>, then point your phone at this code.
          You can scan it in a second app now as a backup.
        </p>

        <div class="mfa-enrollment-qr" *ngIf="qrLoading()">
          <fa-icon [icon]="faSpinnerThird" animation="spin"></fa-icon>
        </div>
        <div class="mfa-enrollment-qr" *ngIf="!qrLoading() && qrDataUrl()">
          <img [src]="qrDataUrl() ?? ''" alt="Two-factor authentication QR code" width="200" height="200">
        </div>

        <details class="mfa-enrollment-manual" [open]="!qrDataUrl() && !qrLoading()">
          <summary>Can't scan? Enter this setup key instead</summary>
          <div class="mfa-enrollment-key">
            <span class="mfa-enrollment-key-icon"><fa-icon [icon]="faKey"></fa-icon></span>
            <code class="mfa-enrollment-key-value">{{ formattedSecretKey }}</code>
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="copySecretKey()" aria-label="Copy setup key">
              <fa-icon [icon]="faCopy"></fa-icon>
            </button>
          </div>
        </details>
      </li>
      <li>
        <p class="mfa-enrollment-step-title">Enter the 6-digit code</p>
        <p class="mfa-enrollment-step-copy">Type the code shown in your app. If it changes, use the new one.</p>
        <form (ngSubmit)="submitCode()">
          <input
            class="form-control mfa-code-input"
            [class.mfa-code-input-error]="errorMessage()"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="7"
            pattern="[0-9 ]*"
            spellcheck="false"
            placeholder="000 000"
            aria-label="6-digit authenticator code"
            [attr.aria-invalid]="errorMessage() ? 'true' : null"
            [ngModel]="code"
            (input)="onCodeInput($event)"
            name="code"
            [disabled]="working()"
          />

          <div class="mfa-enrollment-error" *ngIf="errorMessage()">
            <fa-icon [icon]="faTriangleExclamation"></fa-icon> {{ errorMessage() }}
          </div>

          <div class="mfa-enrollment-actions">
            <button type="button" class="btn btn-outline-dark" *ngIf="showCancel" (click)="cancel()" [disabled]="working()">
              {{ cancelLabel }}
            </button>
            <button type="submit" class="btn btn-primary" [disabled]="working() || !isOtpComplete(code)">
              <fa-icon *ngIf="working()" [icon]="faSpinnerThird" animation="spin"></fa-icon>
              <span>Verify &amp; enable</span>
            </button>
          </div>
        </form>
      </li>
    </ol>
  </ng-container>

  <!-- Step 3: done -->
  <ng-container *ngIf="step() === 'done'">
    <div class="mfa-enrollment-done">
      <span class="mfa-enrollment-done-icon"><fa-icon [icon]="faCircleCheck"></fa-icon></span>
      <p class="mfa-enrollment-done-title">You're ready to sign in securely</p>
      <p class="mfa-enrollment-done-copy">Next time, TRACit will ask for your normal sign-in and a code from your authenticator app.</p>
    </div>

    <ol class="mfa-sign-in-flow" aria-label="How sign-in will work">
      <li>
        <span class="mfa-sign-in-visual mfa-sign-in-visual-password" aria-hidden="true">
          <span></span>
        </span>
        <div>
          <strong>Sign in as usual</strong>
          <span>Use your normal TRACit sign-in method.</span>
        </div>
      </li>
      <li>
        <span class="mfa-sign-in-visual mfa-sign-in-visual-app" aria-hidden="true">
          <span></span>
        </span>
        <div>
          <strong>Open your authenticator app</strong>
          <span>Find the 6-digit code for TRACit.</span>
        </div>
      </li>
      <li>
        <span class="mfa-sign-in-visual mfa-sign-in-visual-check" aria-hidden="true">
          <span></span>
        </span>
        <div>
          <strong>Enter the code</strong>
          <span>TRACit confirms it's you and signs you in.</span>
        </div>
      </li>
    </ol>

    <div class="mfa-enrollment-recovery-note">
      Keep your authenticator app installed. If you lose access to it, use the recovery help on the sign-in screen.
    </div>
  </ng-container>
</div>
